import * as React from 'react';
import {
  MdAdd,
  MdApartment,
  MdBlock,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdEdit,
  MdPauseCircle,
  MdSchedule,
  MdSearch,
  MdVisibility,
} from 'react-icons/md';
import DataGrid from '../../Components/Table/DataGrid';
import DeleteModel from '../../Components/Common/DeleteModel';
import { useNavigate } from "react-router-dom";
import { APPRoutes } from "../../Utils/Route";
import { deleteClientById, getClients } from '../../Services/ClientService';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { toast } from 'react-toastify';
import PageTitle from '../../Components/Common/PageTitle';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';

const STATUS_OPTIONS = ['All', 'Active', 'Inactive', 'Pending', 'Suspended'];
const STATUS_DROPDOWN_OPTIONS = STATUS_OPTIONS.map((option) => ({
  value: option,
  label: option,
}));
const STATUS_BADGE_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text)' },
  Inactive: { backgroundColor: 'var(--hc-status-inactive-bg)', color: 'var(--hc-status-inactive-text)' },
  Suspended: { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-list-bg)', color: 'var(--hc-icon-amber-text)' },
};

export default function ClientsList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewClient = permissionHas(userPermissionCodes, PermissionCodes.CLIENTS_VIEW, decodedToken);
  const canCreateClient = permissionHas(userPermissionCodes, PermissionCodes.CLIENTS_CREATE, decodedToken);
  const canEditClient = permissionHas(userPermissionCodes, PermissionCodes.CLIENTS_EDIT, decodedToken);
  const canDeleteClient = permissionHas(userPermissionCodes, PermissionCodes.CLIENTS_DELETE, decodedToken);

  const [search, setSearch] = React.useState('');
  const [status, setStatus] = React.useState('All');
  const [rows, setRows] = React.useState([]);
  const [statusSummary, setStatusSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState('');

  const handleCreateClient = React.useCallback(() => {
    navigate(APPRoutes.CLIENTS_FORM);
  }, [navigate]);

  const loadClients = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await getClients(token);
      if (response?.is_success) {
        const clients = response?.data?.Clients || [];
        const summary = response?.data?.StatusSummary || null;
        setRows(clients);
        setStatusSummary(summary);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setRows([]);
        setStatusSummary(null);
      } else {
        setRows([]);
        setStatusSummary(null);
      }
    } catch (error) {
      console.error(error);
      setRows([]);
      setStatusSummary(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) {
      loadClients();
    }
  }, [token, loadClients]);

  const handleDeleteClick = React.useCallback((row) => {
    setDeleteError('');
    setDeleteTarget(row);
  }, []);

  const handleDeleteClose = React.useCallback(() => {
    if (deleteLoading) {
      return;
    }

    setDeleteTarget(null);
    setDeleteError('');
  }, [deleteLoading]);

  const handleDeleteConfirm = React.useCallback(async () => {
    if (!deleteTarget?.Id || !token) {
      return;
    }

    try {
      setDeleteLoading(true);
      setDeleteError('');
      const response = await deleteClientById(deleteTarget.Id, token);
      if (response?.is_success) {
        await loadClients();
        setDeleteTarget(null);
      } else if (response?.status_code === 500) {
        toast.error("Internal Server Error. Please Contact Support Team.");
        setDeleteError('Internal Server Error. Please Contact Support Team.');
      } else{
        setDeleteError(response?.message || 'Failed to delete the client. Please try again.');
      }
    } catch (error) {
      console.error(error);
      setDeleteError('Something went wrong while deleting the client.');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, token, loadClients]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();

    return rows.filter((row) => {
      const rowStatus = row.StatusName || row.Status || '';
      const matchesStatus = status === 'All' || rowStatus === status;

      const matchesSearch =
        !term ||
        row.OrganizationName?.toLowerCase().includes(term) ||
        row.ShortForm?.toLowerCase().includes(term) ||
        row.Email?.toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, status]);

  const stats = React.useMemo(() => {
    const counts = statusSummary || rows.reduce((acc, row) => {
      const rowStatus = row.StatusName || row.Status || 'Unknown';
      acc[rowStatus] = (acc[rowStatus] || 0) + 1;
      return acc;
    }, {});

    return [
      {
        label: 'Total Clients',
        value: rows.length,
        icon: <MdApartment size={22} color="var(--hc-icon-blue-text)" />,
        iconBg: 'var(--hc-icon-blue-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Active',
        value: counts.Active || 0,
        icon: <MdCheckCircle size={22} color="var(--hc-status-success-text)" />,
        iconBg: 'var(--hc-icon-green-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
      {
        label: 'Pending',
        value: counts.Pending || 0,
        icon: <MdSchedule size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
      {
        label: 'Inactive',
        value: counts.Inactive || 0,
        icon: <MdPauseCircle size={22} color="var(--hc-status-warning-text)" />,
        iconBg: 'var(--hc-status-warning-bg)',
        accentClass: 'border-secondary',
        valueClass: 'text-secondary',
      },
      {
        label: 'Suspended',
        value: counts.Suspended || 0,
        icon: <MdBlock size={22} color="var(--hc-status-danger-text)" />,
        iconBg: 'var(--hc-status-danger-bg)',
        accentClass: 'border-danger',
        valueClass: 'text-danger',
      },
    ];
  }, [rows, statusSummary]);

  const columns = React.useMemo(() => [
    {
      id: 'OrganizationName',
      label: 'Client',
      minWidth: 180,
      format: (value, row) => {
        const label = row.ShortForm ? `${value} (${row.ShortForm})` : value;
        return canViewClient ? (
          <button
            type="button"
            className="btn p-0 text-start text-dark border-0 bg-transparent"
            style={{
              font: 'inherit'
            }}
            onClick={() =>
              navigate(APPRoutes.CLIENTS_VIEW.replace(":id", row.Id), {
                state: { client: row },
              })
            }
          >
            {label}
          </button>
        ) : (
          <div className="text-secondary">{label}</div>
        );
      },
    },
    {
      id: 'Email',
      label: 'Email',
      minWidth: 180,
    },
    {
      id: 'ServicesCount',
      label: 'Services',
      minWidth: 80,
      align: 'right',
      format: (value) => value ?? 0,
    },
    {
      id: 'StatusName',
      label: 'Status',
      minWidth: 80,
      align: 'center',
      format: (value) => (
        <span
          className="badge rounded-pill fw-bolder px-3 py-2 text-center"
          style={{
            minWidth: '96px',
            fontWeight: 1000,
            ...(STATUS_BADGE_STYLE[value] || {
              backgroundColor: 'var(--hc-status-inactive-bg)',
              color: 'var(--hc-status-inactive-text)',
            }),
          }}
        >
          {value}
        </span>
      ),
    },
    {
      id: 'CreatedBy',
      label: 'CreatedBy',
      minWidth: 140,
    },
    ...(canViewClient || canEditClient || canDeleteClient ? [{
      id: 'actions',
      label: 'Actions',
      minWidth: 80,
      align: 'center',
      format: (_value, row) => (
        <div className="d-flex align-items-center justify-content-center gap-2">
          {canViewClient ? (
            <button
              type="button"
              title="View"
              className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
              onClick={() =>
                navigate(APPRoutes.CLIENTS_VIEW.replace(":id", row.Id), {
                  state: { client: row },
                })
              }
            >
              <MdVisibility size={16} />
            </button>
          ) : null}
          {canEditClient ? (
            <button
              type="button"
              title="Edit"
              className="btn btn-sm p-1 lh-1 hc-text-primary border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-icon-blue-bg)' }}
              onClick={() => navigate(APPRoutes.CLIENTS_EDIT.replace(":id", row.Id))}
            >
              <MdEdit size={16} />
            </button>
          ) : null}
          {canDeleteClient ? (
            <button
              type="button"
              title="Delete"
              className="btn btn-sm p-1 lh-1 text-danger border-0 rounded-2"
              style={{ backgroundColor: 'var(--hc-status-danger-bg)' }}
              onClick={() => handleDeleteClick(row)}
            >
              <MdDelete size={16} />
            </button>
          ) : null}
        </div>
      ),
    }] : []),
  ], [navigate, handleDeleteClick, canViewClient, canEditClient, canDeleteClient]);

  return (
    <>
    <PageTitle title="Clients - Aegis 360"/>

      <div className="d-flex flex-md-row flex-column align-items-start justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1 hc-text-primary fs-3">Clients Management</h4>
          <p className="text-secondary mb-0 small">
            Manage all clients across the Aegis 360 platform
          </p>
        </div>
        {canCreateClient ? (
          <button
            onClick={handleCreateClient}
            type="button"
            className="btn hc-bg-primary mt-md-0 mt-2 w-md-auto btn-sm text-white fw-semibold d-flex align-items-center justify-content-center gap-1 px-3 py-2 rounded-3 text-nowrap"
          >
            <MdAdd size={20} />
            <span>Create Client</span>
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="row g-3 mb-3">
          {[1, 2, 3, 4, 5].map((_, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg">
              <div className="bg-white p-4 rounded-4 shadow">
                <div className="skeleton mb-2" style={{ height: 16, width: "60%" }} />
                <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 28, width: "40%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row g-3 mb-3">
          {stats.map((stat) => (
            <div key={stat.label} className="col-12 col-sm-6 col-lg-4 col-xl">
              <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
                <div
                  className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`}
                />
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-3"
                  style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
                >
                  {stat.icon}
                </div>
                <div className="d-flex flex-column fw-bold lh-sm">
                  <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                    {stat.label}
                  </span>
                  <span className={`fw-bold ${stat.valueClass} fs-5`}>
                    {stat.value}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="row g-2 mb-3">
          <div className="col-12 col-md-9">
            <div className="position-relative">
              <span
                className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none"
                style={{ left: '12px' }}
              >
                <MdSearch size={18} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm hc-input"
                placeholder="Search Client By Name, Short Form or Email"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                style={{ paddingLeft: '36px', paddingRight: '36px', height: '40px' }}
              />
              {search ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  className="btn btn-link bg-danger p-0 position-absolute top-50 translate-middle-y text-light rounded-circle"
                  style={{ right: '8px', lineHeight: 0 }}
                >
                  <MdClose size={18} />
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-12 col-md-3">
            <SearchableDropdown
              placeholder="Select status"
              options={STATUS_DROPDOWN_OPTIONS}
              value={status}
              onChange={setStatus}
              showSearch={true}
              
            />
           
            {/* <FormControl fullWidth size="small">
              <InputLabel id="client-status-label">Status</InputLabel>
              <Select
                labelId="client-status-label"
                label="Status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl> */}
          </div>
        </div>

        <div className="bg-white p-3 rounded shadow">
          {loading ? (
            <div>
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="d-flex gap-3 align-items-center mb-3"
                >
                  <div className="skeleton position-relative overflow-hidden rounded-2 w-25" style={{ height: 16 }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "10%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "15%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "20%" }} />
                  <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 16, width: "10%" }} />
                </div>
              ))}
            </div>
          ) : (
            <DataGrid
              columns={columns}
              rows={filteredRows}
              defaultRowsPerPage={10}
              stickyHeader
              getRowId={(row) => row.Id}
            />
          )}
        </div>
      </div>

      <DeleteModel
        isOpen={Boolean(deleteTarget)}
        title="Delete Client"
        entityLabel="client"
        itemName={deleteTarget?.OrganizationName || deleteTarget?.ShortForm || ''}
        description="You are about to remove this client and its access from the platform. This action is permanent."
        confirmText="Delete Client"
        isLoading={deleteLoading}
        errorMessage={deleteError}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
}